"use client";
import { type FeatureSlideData } from "@gettactic/defines/src/feature-announcements";
import { useCallback, useEffect, useState } from "react";

import {
  useUnseenFeatures,
  type UseUnseenFeaturesOptions
} from "./useUnseenFeatures";

export type FeatureModalMode = "single" | "browse-all";

export const useFeatureAnnouncementSession = (
  options: UseUnseenFeaturesOptions = {}
) => {
  const {
    availableFeatures,
    unseenFeatures,
    hasUnseenFeatures,
    nextUnseenFeature,
    unseenCount,
    isLoaded,
    markFeatureAsSeen
  } = useUnseenFeatures(options);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentFeature, setCurrentFeature] = useState<FeatureSlideData | null>(
    null
  );
  const [allFeatures, setAllFeatures] = useState<FeatureSlideData[] | null>(
    null
  );
  const [modalMode, setModalMode] = useState<FeatureModalMode>("single");
  const [userClosedModal, setUserClosedModal] = useState(false);

  // Auto-show modal if there are unseen features and user hasn't closed it
  useEffect(() => {
    if (
      isLoaded &&
      hasUnseenFeatures &&
      nextUnseenFeature &&
      !userClosedModal &&
      !isModalOpen
    ) {
      setCurrentFeature(nextUnseenFeature);
      setAllFeatures(null);
      setModalMode("single");
      setIsModalOpen(true);
    }
  }, [isLoaded, hasUnseenFeatures, nextUnseenFeature, userClosedModal, isModalOpen]);

  // Close modal and mark feature as read (for Try/Maybe Later buttons)
  // In single mode with "Maybe Later", show next unseen feature if available
  // In browse-all mode, featureId should be passed from the modal
  const handleDismissFeature = useCallback(
    (featureId?: string, action?: "try" | "maybe-later") => {
      const idToMark = featureId ?? currentFeature?.id;
      if (idToMark) {
        markFeatureAsSeen(idToMark);
      }

      // In single mode with "Maybe Later", try to show the next unseen feature
      if (modalMode === "single" && action === "maybe-later") {
        // Find the next unseen feature (excluding the one we just marked)
        const remainingUnseen = unseenFeatures.filter((f) => f.id !== idToMark);
        if (remainingUnseen.length > 0) {
          setCurrentFeature(remainingUnseen[0]);
          return;
        }
      }

      setUserClosedModal(true);
      setIsModalOpen(false);
      setCurrentFeature(null);
      setAllFeatures(null);
    },
    [currentFeature, markFeatureAsSeen, modalMode, unseenFeatures]
  );

  // Close modal without marking as read (for X button, ESC, outside click)
  const handleCloseModal = useCallback(() => {
    setUserClosedModal(true);
    setIsModalOpen(false);
    setCurrentFeature(null);
    setAllFeatures(null);
  }, []);

  // Open modal with a single feature
  const openFeaturesModal = useCallback(
    (feature?: FeatureSlideData) => {
      const featureToShow = feature ?? nextUnseenFeature ?? unseenFeatures[0];
      if (featureToShow) {
        setCurrentFeature(featureToShow);
        setAllFeatures(null);
        setModalMode("single");
        setIsModalOpen(true);
      }
    },
    [nextUnseenFeature, unseenFeatures]
  );

  // Open modal to browse all features (with navigation)
  // Unseen features are shown first, then seen features
  const openAllFeaturesModal = useCallback(() => {
    // Nothing to browse (e.g. announcements are globally disabled, or the
    // viewer's org/role has none available) — leave the modal closed rather
    // than opening it empty.
    if (availableFeatures.length === 0) return;
    const unseenIds = new Set(unseenFeatures.map((f) => f.id));
    const sortedFeatures = [
      ...availableFeatures.filter((f) => unseenIds.has(f.id)),
      ...availableFeatures.filter((f) => !unseenIds.has(f.id))
    ];
    setCurrentFeature(null);
    setAllFeatures(sortedFeatures);
    setModalMode("browse-all");
    setIsModalOpen(true);
  }, [availableFeatures, unseenFeatures]);

  return {
    isModalOpen,
    currentFeature,
    allFeatures,
    modalMode,
    unseenFeatures,
    hasUnseenFeatures,
    unseenCount,
    isLoaded,
    handleCloseModal,
    handleDismissFeature,
    openFeaturesModal,
    openAllFeaturesModal,
    markFeatureAsSeen
  };
};
